import { type PropsWithChildren } from 'react'
import { type SystemCssProperties } from '@dhl-ui/theme'
import { useIsMobileVariant } from '../../theme/hooks/use-is-mobile-variant'
import { Box } from '../../primitives/Box'
import { Paper } from '../../primitives/Paper'
import { type BaseProps } from '../../types'
import { spacing } from '../../theme'
import { CardProvider } from './Card/CardProvider'

export type CardProps = PropsWithChildren<{
  /**
   * Will format a dense variant
   */
  isDense?: boolean

  /**
   * Only for our `desktop` variant its possible you can set the specific divider(s).
   * By default both are vissible
   */
  dividers?: [] | ['top'] | ['bottom'] | ['top', 'bottom']

  /**
   * Only for our `desktop` variant the default is that we show the card rounded, you can turn if off
   * This has no influence on our mobile variant, on mobile we never show it rounded
   *
   * @default true
   */
  isRounded?: boolean

  /**
   * Only for our `desktop` variant the default is that we show the card rounded, you can turn if off
   * This has no influence on our mobile variant, on mobile we never show it rounded
   *
   * @default true
   */
  isOutlined?: boolean

  /**
   * Only for our `mobile` variant we can give the top a little more spacing
   *
   * @default false
   */
  hasPaddingTop?: boolean

  /**
   * Only for our `mobile` variant we can give the bottom a little more spacing
   *
   * @default false
   */
  hasPaddingBottom?: boolean

  /**
   * Option to get a 'dark' background instead of its default 'light'
   * @default 'light
   */
  background?: 'light' | 'dark'

  /**
   * When your inner content should be able to overflow or not
   * @default true
   */
  hasOverflowHidden?: boolean
}> &
  BaseProps<
    HTMLDivElement,
    never,
    Pick<
      SystemCssProperties,
      // no overflow is need this is done with prop hasOverflowHidden
      'height' | 'maxHeight' | 'minHeight' | 'pointerEvents'
    >
  >

/**
 * Private props, not part of bleeding to public api!
 */
type InternalCardProps = object

export const CardInternal = ({
  children,
  isDense = false,
  dividers = ['top', 'bottom'],
  hasOverflowHidden = true,
  isRounded = true,
  isOutlined = true,
  hasPaddingTop,
  hasPaddingBottom,
  background = 'light',
  sx,
  ...props
}: CardProps & InternalCardProps) => {
  const isMobile = useIsMobileVariant()

  return (
    <CardProvider isDense={isDense} dividers={dividers}>
      {isMobile ? (
        <Box
          sx={{
            ...sx,
            backgroundColor: background === 'light' ? 'background.paper' : 'background.default',
            p: spacing(hasPaddingTop ? 1 : 0, 0, hasPaddingBottom ? 1 : 0, 0),
            display: 'flex',
            flexDirection: 'column',
            overflow: hasOverflowHidden ? 'hidden' : undefined,
          }}
          {...props}
        >
          {children}
        </Box>
      ) : (
        <Paper
          isOutlined={isOutlined}
          isRounded={isRounded}
          hasNoOverflowHidden={!hasOverflowHidden}
          sx={{ ...sx, display: 'flex', flexDirection: 'column' }}
          background={background}
          {...props}
        >
          {children}
        </Paper>
      )}
    </CardProvider>
  )
}
